import type {
  RecurringInvoiceDetailResponseDto,
  RecurringInvoiceResponseDto,
  RecurringInvoicesControllerListParams,
  UpdateRecurringInvoiceStatusDto,
} from '@trellis/api'
import type { CreateRecurringInvoice, UpdateRecurringInvoice } from '@trellis/shared'

import { apiClient } from '@/lib/api-client'
import { request, requestPaged } from '@/lib/request'

export interface TriggerRunResult {
  skipped: boolean
  success: boolean
  invoiceId?: string
  error?: string
}

export const listRecurringInvoices = (
  params: RecurringInvoicesControllerListParams = {},
) =>
  requestPaged<RecurringInvoiceResponseDto>(() =>
    apiClient.api.recurringInvoicesControllerList(params),
  )

export const getRecurringInvoice = (id: string) =>
  request<RecurringInvoiceDetailResponseDto>(() =>
    apiClient.api.recurringInvoicesControllerGetOne({ id }),
  )

export const createRecurringInvoice = (data: CreateRecurringInvoice) => {
  const { notes, ...rest } = data
  return request<RecurringInvoiceResponseDto>(() =>
    apiClient.api.recurringInvoicesControllerCreate({
      ...rest,
      notes: notes ?? undefined,
    }),
  )
}

export const updateRecurringInvoice = (id: string, data: UpdateRecurringInvoice) => {
  const { notes, ...rest } = data
  return request<RecurringInvoiceResponseDto>(() =>
    apiClient.api.recurringInvoicesControllerUpdate(
      { id },
      { ...rest, notes: notes ?? undefined },
    ),
  )
}

export const updateRecurringInvoiceStatus = (
  id: string,
  data: UpdateRecurringInvoiceStatusDto,
) =>
  request<RecurringInvoiceResponseDto>(() =>
    apiClient.api.recurringInvoicesControllerUpdateStatus({ id }, data),
  )

export const deleteRecurringInvoice = (id: string) =>
  request<void>(() => apiClient.api.recurringInvoicesControllerRemove({ id }))

// TODO: replace with generated method (e.g. apiClient.api.recurringInvoicesControllerTrigger)
// after running `turbo run generate --filter=@trellis/api` with the backend running on this branch
export const triggerRecurringInvoice = (id: string) =>
  request<TriggerRunResult>(() =>
    apiClient.api.request({
      path: `/api/recurring-invoices/${id}/trigger`,
      method: 'POST',
      secure: true,
      format: 'json',
    }),
  )

export const getRecurringInvoiceStats = () =>
  request<{ total: number; active: number; paused: number }>(() =>
    apiClient.api.request({
      path: `/api/recurring-invoices/stats`,
      method: 'GET',
      secure: true,
      format: 'json',
    }),
  )
